import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  AttachMoney as CashIcon,
  SwapHoriz as TransferIcon,
  Add as AddIcon,
  Refresh as ReconcileIcon,
  History as HistoryIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from '../../utils/i18n-utils';
import { format, parseISO } from 'date-fns';
import * as cashManagementApi from '../../api/cashManagement';
import { CashBalance, CashTransaction } from '../../api/cashManagement';

const CashManagement: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(false);
  const [balances, setBalances] = useState<CashBalance | null>(null);
  const [transactions, setTransactions] = useState<CashTransaction[]>([]);
  const [openTransferDialog, setOpenTransferDialog] = useState<boolean>(false);
  const [transactionType, setTransactionType] = useState<CashTransaction['type']>('deposit');
  const [transactionAmount, setTransactionAmount] = useState<number>(0);
  const [transactionDate, setTransactionDate] = useState<Date | null>(new Date());
  const [transactionReference, setTransactionReference] = useState<string>('');
  const [transactionNotes, setTransactionNotes] = useState<string>('');
  const [balanceHistoryDialogOpen, setBalanceHistoryDialogOpen] = useState<boolean>(false);
  const [balanceHistory, setBalanceHistory] = useState<CashBalance[]>([]);
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState<boolean>(false);
  const [balanceHistoryStartDate, setBalanceHistoryStartDate] = useState<Date | null>(null);
  const [balanceHistoryEndDate, setBalanceHistoryEndDate] = useState<Date | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Function to fetch current cash balance
  const fetchCashBalance = async () => {
    setBalanceLoading(true);
    try {
      const data = await cashManagementApi.getCurrentCashBalance();
      setBalances(data);
    } catch (error) {
      console.error('Error fetching cash balance:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load cash balance',
        severity: 'error'
      });
    } finally {
      setBalanceLoading(false);
    }
  };
  
  // Function to fetch transaction history
  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const data = await cashManagementApi.getCashTransactions();
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load transaction history',
        severity: 'error'
      });
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Load balance and transactions in parallel
      await Promise.all([
        fetchCashBalance(),
        fetchTransactions()
      ]);
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  const handleOpenTransferDialog = (type: CashTransaction['type']) => {
    setTransactionType(type);
    setTransactionAmount(0);
    setTransactionDate(new Date());
    setTransactionReference('');
    setTransactionNotes('');
    setOpenTransferDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenTransferDialog(false);
  };

  const handleTransactionSubmit = async () => {
    if (!transactionAmount || !transactionDate) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    // Validate transaction amount is positive
    if (transactionAmount <= 0) {
      setSnackbar({
        open: true,
        message: 'Amount must be greater than zero',
        severity: 'error'
      });
      return;
    }

    // For withdrawals, check if there's enough money in the safe
    if (transactionType === 'withdraw' && balances && transactionAmount > balances.safeBalance) {
      setSnackbar({
        open: true,
        message: 'Not enough money in the safe',
        severity: 'error'
      });
      return;
    }

    // For transfers to bank, check if there's enough money in the safe
    if (transactionType === 'transfer_to_bank' && balances && transactionAmount > balances.safeBalance) {
      setSnackbar({
        open: true,
        message: 'Not enough money in the safe to transfer',
        severity: 'error'
      });
      return;
    }

    // For transfers from bank, check if there's enough money in the bank
    if (transactionType === 'transfer_from_bank' && balances && transactionAmount > balances.bankBalance) {
      setSnackbar({
        open: true,
        message: 'Not enough money in the bank to transfer',
        severity: 'error'
      });
      return;
    }

    // Create transaction object
    const transaction: Partial<CashTransaction> = {
      date: format(transactionDate!, 'yyyy-MM-dd'),
      type: transactionType,
      amount: transactionAmount,
      reference: transactionReference,
      notes: transactionNotes
    };

    try {
      // Call API to record transaction
      await cashManagementApi.recordCashTransaction(transaction as CashTransaction);
      
      // Refresh data
      await Promise.all([
        fetchCashBalance(),
        fetchTransactions()
      ]);
      
      setSnackbar({
        open: true,
        message: 'Transaction recorded successfully',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error recording transaction:', error);
      setSnackbar({
        open: true,
        message: 'Failed to record transaction',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleOpenBalanceHistory = async () => {
    setBalanceHistoryDialogOpen(true);
    await fetchBalanceHistory();
  };

  const handleCloseBalanceHistory = () => {
    setBalanceHistoryDialogOpen(false);
    setBalanceHistory([]);
    setBalanceHistoryStartDate(null);
    setBalanceHistoryEndDate(null);
  };

  const fetchBalanceHistory = async () => {
    try {
      setBalanceHistoryLoading(true);
      const startDate = balanceHistoryStartDate ? format(balanceHistoryStartDate, 'yyyy-MM-dd') : undefined;
      const endDate = balanceHistoryEndDate ? format(balanceHistoryEndDate, 'yyyy-MM-dd') : undefined;
      const data = await cashManagementApi.getBalanceHistory(startDate, endDate);
      setBalanceHistory(data);
    } catch (error) {
      console.error('Error fetching balance history:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load balance history',
        severity: 'error'
      });
    } finally {
      setBalanceHistoryLoading(false);
    }
  };
  
  // Function to refresh data
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchCashBalance(),
      fetchTransactions()
    ]);
    setLoading(false);
  };
  
  // Function to handle reconciliation
  const handleReconcile = async () => {
    try {
      // Update current balance with confirmation
      if (balances) {
        await cashManagementApi.updateBalance({
          safeBalance: balances.safeBalance,
          bankBalance: balances.bankBalance,
          notes: 'Manual reconciliation',
          date: format(new Date(), 'yyyy-MM-dd')
        });
        
        await fetchCashBalance();
        
        setSnackbar({
          open: true,
          message: 'Balance reconciled successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error reconciling balance:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reconcile balance',
        severity: 'error'
      });
    }
  };

  const getTransactionTypeLabel = (type: CashTransaction['type']): string => {
    switch (type) {
      case 'deposit':
        return t('cashCount.depositCash');
      case 'withdraw':
        return t('cashCount.withdrawCash');
      case 'transfer_to_bank':
        return t('cashCount.transferToBank');
      case 'transfer_from_bank':
        return t('cashCount.transferFromBank');
      default:
        return type;
    }
  };

  // Show loading spinner when first loading the page
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            mb: 0.5,
            color: '#101D68',
          }}
        >
          {t('navigation.accounting.cashManagement')}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#6B7280',
          }}
        >
          Monitor and manage cash balances and transactions
        </Typography>
      </Box>

      {/* Cash Balances Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative',
            borderRadius: 3,
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0px 8px 24px rgba(16, 29, 104, 0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            {balanceLoading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 1, backdropFilter: 'blur(4px)' }}>
                <CircularProgress size={32} sx={{ color: '#101D68' }} />
              </Box>
            )}
            <CardHeader 
              title={t('cashCount.safeBalance')}
              avatar={<CashIcon sx={{ color: '#101D68' }} />}
              sx={{
                '& .MuiCardHeader-title': {
                  fontWeight: 600,
                  color: '#101D68',
                },
              }}
            />
            <CardContent>
              <Typography 
                variant="h3" 
                component="div" 
                align="center"
                sx={{
                  fontWeight: 700,
                  color: '#101D68',
                }}
              >
                د.ل{balances?.safeBalance.toFixed(2) || '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative',
            borderRadius: 3,
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0px 8px 24px rgba(16, 29, 104, 0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            {balanceLoading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 1, backdropFilter: 'blur(4px)' }}>
                <CircularProgress size={32} sx={{ color: '#101D68' }} />
              </Box>
            )}
            <CardHeader 
              title={t('cashCount.bankBalance')}
              avatar={<BankIcon sx={{ color: '#101D68' }} />}
              sx={{
                '& .MuiCardHeader-title': {
                  fontWeight: 600,
                  color: '#101D68',
                },
              }}
            />
            <CardContent>
              <Typography 
                variant="h3" 
                component="div" 
                align="center"
                sx={{
                  fontWeight: 700,
                  color: '#101D68',
                }}
              >
                د.ل{balances?.bankBalance.toFixed(2) || '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative',
            borderRadius: 3,
            boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0px 8px 24px rgba(16, 29, 104, 0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            {balanceLoading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 1, backdropFilter: 'blur(4px)' }}>
                <CircularProgress size={32} sx={{ color: '#101D68' }} />
              </Box>
            )}
            <CardHeader 
              title={t('cashCount.totalCash')}
              avatar={<TransferIcon sx={{ color: '#101D68' }} />}
              sx={{
                '& .MuiCardHeader-title': {
                  fontWeight: 600,
                  color: '#101D68',
                },
              }}
            />
            <CardContent>
              <Typography 
                variant="h3" 
                component="div" 
                align="center"
                sx={{
                  fontWeight: 700,
                  color: '#101D68',
                }}
              >
                د.ل{balances ? (balances.safeBalance + balances.bankBalance).toFixed(2) : '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
      }}>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenTransferDialog('deposit')}
          >
            {t('cashCount.depositCash')}
          </Button>
          <Button 
            variant="contained"
            color="secondary"
            startIcon={<TransferIcon />}
            onClick={() => handleOpenTransferDialog('withdraw')}
          >
            {t('cashCount.withdrawCash')}
          </Button>
          <Button 
            variant="contained"
            color="primary"
            startIcon={<BankIcon />}
            onClick={() => handleOpenTransferDialog('transfer_to_bank')}
          >
            {t('cashCount.transferToBank')}
          </Button>
          <Button 
            variant="contained"
            color="info"
            startIcon={<CashIcon />}
            onClick={() => handleOpenTransferDialog('transfer_from_bank')}
          >
            {t('cashCount.transferFromBank')}
          </Button>
          <Button 
            variant="contained"
            color="success"
            component="a"
            href="/dashboard/accounting/cash-count"
            startIcon={<CalculateIcon />}
          >
            {t('cashCount.endOfDayCount') || 'End of Day Count'}
          </Button>
          <Button 
            variant="outlined"
            startIcon={<ReconcileIcon />}
            onClick={handleReconcile}
          >
            {t('cashCount.reconcile')}
          </Button>
          <Button 
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleOpenBalanceHistory}
          >
            {t('cashCount.balanceHistory') || 'Balance History'}
          </Button>
        </Stack>
      </Paper>

      {/* Recent Transactions */}
      <Paper sx={{ mb: 3, position: 'relative' }}>
        {transactionsLoading && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{t('cashCount.cashFlow')}</Typography>
        </Box>
        <Divider />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('cashCount.date')}</TableCell>
              <TableCell>{t('common.type')}</TableCell>
              <TableCell align="right">{t('expenses.amount')}</TableCell>
              <TableCell>{t('cashCount.transferReference')}</TableCell>
              <TableCell>{t('cashCount.notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{format(new Date(transaction.date), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>{getTransactionTypeLabel(transaction.type)}</TableCell>
                  <TableCell align="right">د.ل{transaction.amount.toFixed(2)}</TableCell>
                  <TableCell>{transaction.reference}</TableCell>
                  <TableCell>{transaction.notes}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No transactions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Transaction Dialog */}
      <Dialog open={openTransferDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {getTransactionTypeLabel(transactionType)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>{t('common.type')}</InputLabel>
                <Select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value as CashTransaction['type'])}
                  label={t('common.type')}
                >
                  <MenuItem value="deposit">{t('cashCount.depositCash')}</MenuItem>
                  <MenuItem value="withdraw">{t('cashCount.withdrawCash')}</MenuItem>
                  <MenuItem value="transfer_to_bank">{t('cashCount.transferToBank')}</MenuItem>
                  <MenuItem value="transfer_from_bank">{t('cashCount.transferFromBank')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('cashCount.transferAmount')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(parseFloat(e.target.value) || 0)}
                fullWidth
                required
                InputProps={{
                  startAdornment: "د.ل",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DatePicker
                label={t('cashCount.transferDate')}
                value={transactionDate}
                onChange={(date) => setTransactionDate(date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('cashCount.transferReference')}
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('cashCount.notes')}
                value={transactionNotes}
                onChange={(e) => setTransactionNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleTransactionSubmit} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Balance History Dialog */}
      <Dialog 
        open={balanceHistoryDialogOpen} 
        onClose={handleCloseBalanceHistory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('cashCount.balanceHistory') || 'Cash Balance History'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label={t('cashCount.startDate') || 'Start Date'}
              value={balanceHistoryStartDate}
              onChange={(newValue) => setBalanceHistoryStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label={t('cashCount.endDate') || 'End Date'}
              value={balanceHistoryEndDate}
              onChange={(newValue) => setBalanceHistoryEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button 
              variant="outlined" 
              onClick={fetchBalanceHistory} 
              disabled={balanceHistoryLoading}
            >
              {t('common.filter') || 'Filter'}
            </Button>
          </Box>

          {balanceHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('cashCount.date') || 'Date'}</TableCell>
                    <TableCell align="right">{t('cashCount.safeBalance') || 'Safe Balance'}</TableCell>
                    <TableCell align="right">{t('cashCount.bankBalance') || 'Bank Balance'}</TableCell>
                    <TableCell align="right">{t('cashCount.total') || 'Total'}</TableCell>
                    <TableCell>{t('cashCount.notes') || 'Notes'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balanceHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {t('cashCount.noHistory') || 'No balance history found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    balanceHistory.map((balance) => (
                      <TableRow key={balance.id}>
                        <TableCell>{format(new Date(balance.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell align="right">د.ل{balance.safeBalance.toFixed(2)}</TableCell>
                        <TableCell align="right">د.ل{balance.bankBalance.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <strong>د.ل{(balance.safeBalance + balance.bankBalance).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell>{balance.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBalanceHistory}>
            {t('common.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashManagement; 